import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle } from 'lucide-react';

import { Modal } from '../ui';
import { cn } from '../../lib/cn';

/**
 * ModifiersHint — small floating "?" help button at the top-center of the
 * canvas. Click to open a Keyboard Shortcuts modal. The full shortcut list
 * used to be a wide always-visible pill; that ate too much horizontal real
 * estate on smaller viewports.
 */
interface ModifiersHintProps {
    isDarkMode?: boolean;
}

export default function ModifiersHint({ isDarkMode = false }: ModifiersHintProps) {
    const { t } = useTranslation('canvas');
    const [open, setOpen] = useState(false);

    const kbd = cn(
        'inline-block px-1.5 py-0.5 rounded text-[11px] font-mono font-semibold border min-w-[28px] text-center',
        isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-100' : 'bg-gray-100 border-gray-300 text-gray-800',
    );

    const rows: Array<{ key: string; label: string }> = [
        { key: 'Space', label: t('hint_pan', { defaultValue: 'Pan the canvas' }) },
        { key: '⇧ Shift+drag', label: t('hint_draw_over', { defaultValue: 'Draw a new box over an existing one' }) },
        { key: 'Wheel', label: t('hint_scroll', { defaultValue: 'Pan with the mouse wheel / trackpad' }) },
        { key: 'Ctrl+Wheel', label: t('hint_zoom', { defaultValue: 'Zoom with the mouse wheel / pinch' }) },
        { key: 'D / Del', label: t('hint_delete', { defaultValue: 'Delete the selected box' }) },
        { key: 'Esc', label: t('hint_esc', { defaultValue: 'Deselect / exit Pan or Draw mode' }) },
        { key: 'Enter', label: t('hint_next', { defaultValue: 'Next image' }) },
        { key: '⇧ Enter', label: t('hint_prev', { defaultValue: 'Previous image' }) },
        { key: '1 … 9', label: t('hint_class', { defaultValue: 'Pick the first 9 classes' }) },
        { key: 'Ctrl+Z', label: t('hint_undo', { defaultValue: 'Undo the last action' }) },
        { key: 'Ctrl+S', label: t('hint_save', { defaultValue: 'Flush pending saves immediately' }) },
    ];

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                title={t('keyboard_shortcuts', { defaultValue: 'Keyboard shortcuts' })}
                aria-label={t('keyboard_shortcuts', { defaultValue: 'Keyboard shortcuts' })}
                className={cn(
                    'absolute top-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center justify-center h-8 w-8 rounded-full border backdrop-blur-sm shadow-sm transition-colors',
                    isDarkMode
                        ? 'bg-gray-900/80 border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-gray-100'
                        : 'bg-white/85 border-gray-200 text-gray-600 hover:bg-white hover:text-gray-900',
                )}
            >
                <HelpCircle className="h-4 w-4" />
            </button>
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title={t('keyboard_shortcuts', { defaultValue: 'Keyboard shortcuts' })}
                widthClass="max-w-md"
                isDarkMode={isDarkMode}
            >
                <ul className="space-y-2.5">
                    {rows.map((row) => (
                        <li key={row.key} className="flex items-center gap-3">
                            <span className={kbd}>{row.key}</span>
                            <span className={cn('text-sm', isDarkMode ? 'text-gray-300' : 'text-gray-700')}>
                                {row.label}
                            </span>
                        </li>
                    ))}
                </ul>
            </Modal>
        </>
    );
}
